import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

const widths = [320, 480, 640, 960, 1280, 1600, 1920, 2560, 3200, 3840, 5120, 5760];

export const imageSources = (src) => {
  if (!src?.startsWith('https://images.unsplash.com/')) return undefined;
  const original = new URL(src);
  const width = Number(original.searchParams.get('w'));
  const height = Number(original.searchParams.get('h'));
  return widths.map(size => {
    const image = new URL(original);
    image.searchParams.set('w', size);
    if (width && height) image.searchParams.set('h', Math.round(size * height / width));
    image.searchParams.set('auto', 'format');
    image.searchParams.set('q', '85');
    return `${image.href} ${size}w`;
  }).join(', ');
};

export const ResponsiveImage = ({ src, alt, sizes = '100vw', loading = 'lazy', onLoad, ...props }) => {
  const id = useId().replace(/[^a-zA-Z0-9-]/g, '');
  const element = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(null);
  const measure = useCallback(() => {
    const image = element.current;
    if (!image) return;
    const bounds = image.getBoundingClientRect();
    if (!bounds.width) return;
    const ratio = image.naturalHeight ? image.naturalWidth / image.naturalHeight : 0;
    // Cover can enlarge a landscape photo far beyond the width of a tall mobile box.
    const requiredWidth = getComputedStyle(image).objectFit === 'cover' && ratio
      ? Math.max(bounds.width, bounds.height * ratio) : bounds.width;
    const rounded = Math.ceil(requiredWidth / 16) * 16;
    setMeasuredWidth(previous => previous && Math.abs(previous - rounded) < 32 ? previous : rounded);
  }, []);
  useEffect(() => {
    setMeasuredWidth(null);
    const observer = new ResizeObserver(measure);
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [src, measure]);
  return <img ref={element} data-testid={`responsive-image-${id}`} src={src} srcSet={imageSources(src)}
    sizes={measuredWidth ? `${measuredWidth}px` : sizes} loading={loading} decoding="async" alt={alt}
    onLoad={event => { measure(); onLoad?.(event); }} {...props} />;
};